import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { 
  LogOut, Plus, Edit, Trash2, GripVertical, Save, X, 
  Image, Video, Star, Flame, Leaf, Heart, ChefHat,
  FolderOpen, ArrowLeft, RefreshCw, Eye, BarChart3, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AnalyticsPanel from './AnalyticsPanel';
import ImageUploadField from './ImageUploadField';
import CampaignPanel from './CampaignPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ token, onLogout }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [activeTab, setActiveTab] = useState('menu'); // 'menu' | 'analytics'

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/categories`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [token]);

  // Fetch items for selected category
  const fetchItems = useCallback(async (categoryId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/items/${categoryId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories().finally(() => setLoading(false));
  }, [fetchCategories]);

  useEffect(() => {
    if (selectedCategory) {
      fetchItems(selectedCategory.category_id);
    }
  }, [selectedCategory, fetchItems]);

  // Category CRUD
  const saveCategory = async (category) => {
    try {
      const isNew = !editingCategory?.id;
      const url = isNew 
        ? `${API_URL}/api/admin/categories`
        : `${API_URL}/api/admin/categories/${editingCategory.category_id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers,
        body: JSON.stringify(category),
      });

      if (response.ok) {
        toast.success(isNew ? 'Kategori eklendi!' : 'Kategori güncellendi!');
        fetchCategories();
        setShowCategoryModal(false);
        setEditingCategory(null);
      } else {
        throw new Error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu!');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Bu kategoriyi ve içindeki tüm ürünleri silmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers,
      });
      if (response.ok) {
        toast.success('Kategori silindi!');
        fetchCategories();
        if (selectedCategory?.category_id === categoryId) {
          setSelectedCategory(null);
          setItems([]);
        }
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız!');
    }
  };

  // Item CRUD
  const saveItem = async (item) => {
    try {
      const isNew = !editingItem?.id;
      const url = isNew 
        ? `${API_URL}/api/admin/items/${selectedCategory.category_id}`
        : `${API_URL}/api/admin/items/${editingItem.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers,
        body: JSON.stringify(item),
      });

      if (response.ok) {
        toast.success(isNew ? 'Ürün eklendi!' : 'Ürün güncellendi!');
        fetchItems(selectedCategory.category_id);
        setShowItemModal(false);
        setEditingItem(null);
      } else {
        throw new Error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu!');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/items/${itemId}`, {
        method: 'DELETE',
        headers,
      });
      if (response.ok) {
        toast.success('Ürün silindi!');
        fetchItems(selectedCategory.category_id);
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız!');
    }
  };

  // Drag and drop handlers
  const handleCategoryDragEnd = async (result) => {
    if (!result.destination) return;
    
    const reordered = Array.from(categories);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    
    setCategories(reordered);
    
    try {
      await fetch(`${API_URL}/api/admin/categories/reorder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: reordered.map((cat, index) => ({ id: cat.id, order: index })),
        }),
      });
      toast.success('Sıralama güncellendi!');
    } catch (error) {
      toast.error('Sıralama güncellenemedi!');
      fetchCategories();
    }
  };

  const handleItemDragEnd = async (result) => {
    if (!result.destination) return;
    
    const reordered = Array.from(items);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    
    setItems(reordered);
    
    try {
      await fetch(`${API_URL}/api/admin/items/reorder/${selectedCategory.category_id}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: reordered.map((item, index) => ({ id: item.id, order: index })),
        }),
      });
      toast.success('Sıralama güncellendi!');
    } catch (error) {
      toast.error('Sıralama güncellenemedi!');
      fetchItems(selectedCategory.category_id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {selectedCategory && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSelectedCategory(null);
                  setItems([]);
                }}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <h1 className="text-xl font-display text-primary">
              {selectedCategory ? selectedCategory.name.tr : 'Gomşu Admin Paneli'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/menu', '_blank', 'noopener,noreferrer')}
              data-testid="admin-preview-menu-btn"
              aria-label="Menüyü yeni sekmede önizle"
              title="Menüyü yeni sekmede önizle"
            >
              <Eye className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Menüyü Önizle</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchCategories();
                if (selectedCategory) fetchItems(selectedCategory.category_id);
                toast.success('Veriler yenilendi!');
              }}
              data-testid="admin-refresh-btn"
              aria-label="Verileri yenile"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Yenile</span>
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                localStorage.removeItem('adminToken');
                onLogout();
              }}
              data-testid="admin-logout-btn"
              aria-label="Çıkış yap"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Çıkış</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-border/40">
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'menu' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="admin-tab-menu"
          >
            <FolderOpen className="w-4 h-4" />
            Menü Yönetimi
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'analytics' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="admin-tab-analytics"
          >
            <BarChart3 className="w-4 h-4" />
            İstatistikler
          </button>
          <button
            onClick={() => setActiveTab('campaign')}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'campaign' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="admin-tab-campaign"
          >
            <Megaphone className="w-4 h-4" />
            Banner
          </button>
        </div>

        {activeTab === 'analytics' ? (
          <AnalyticsPanel token={token} />
        ) : activeTab === 'campaign' ? (
          <CampaignPanel token={token} />
        ) : !selectedCategory ? (
          // Categories View
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-foreground">Kategoriler</h2>
              <Button
                onClick={() => {
                  setEditingCategory({
                    category_id: '',
                    name: { tr: '', en: '' },
                    description: { tr: '', en: '' },
                    image: '',
                  });
                  setShowCategoryModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Kategori
              </Button>
            </div>

            {categories.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Henüz kategori yok</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Mevcut menü verilerini yüklemek için aşağıdaki butona tıklayın
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => window.location.href = '/admin/seed'}
                  >
                    Veri Aktar
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <DragDropContext onDragEnd={handleCategoryDragEnd}>
                <Droppable droppableId="categories">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                      {categories.map((category, index) => (
                        <Draggable key={category.id} draggableId={category.id} index={index}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`transition-shadow ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                            >
                              <CardContent className="p-4 flex items-center gap-4">
                                <div {...provided.dragHandleProps} className="cursor-grab">
                                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <img
                                  src={category.image}
                                  alt={category.name.tr}
                                  className="w-16 h-16 rounded-lg object-cover"
                                  onError={(e) => { if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) e.currentTarget.src = '/images/menu/placeholder.webp'; }}
                                />
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-foreground truncate">
                                    {category.name.tr}
                                  </h3>
                                  <p className="text-sm text-muted-foreground truncate">
                                    {category.description.tr}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedCategory(category)}
                                  >
                                    <FolderOpen className="w-4 h-4 mr-2" />
                                    Ürünler
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setEditingCategory(category);
                                      setShowCategoryModal(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => deleteCategory(category.category_id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        ) : (
          // Items View
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-foreground">
                {selectedCategory.name.tr} - Ürünler ({items.length})
              </h2>
              <Button
                onClick={() => {
                  setEditingItem({
                    name: { tr: '', en: '' },
                    description: { tr: '', en: '' },
                    price: 0,
                    image: '',
                    allergens: [],
                    isPopular: false,
                    isSignature: false,
                    isSpicy: false,
                    isVegan: false,
                    isHealthy: false,
                    hasVideo: false,
                    video: '',
                  });
                  setShowItemModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ürün
              </Button>
            </div>

            {items.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">Bu kategoride henüz ürün yok</p>
                </CardContent>
              </Card>
            ) : (
              <DragDropContext onDragEnd={handleItemDragEnd}>
                <Droppable droppableId="items">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                      {items.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`transition-shadow ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                            >
                              <CardContent className="p-4 flex items-center gap-4">
                                <div {...provided.dragHandleProps} className="cursor-grab">
                                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <img
                                  src={item.image}
                                  alt={item.name.tr}
                                  className="w-16 h-16 rounded-lg object-cover"
                                  onError={(e) => { if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) e.currentTarget.src = '/images/menu/placeholder.webp'; }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-medium text-foreground truncate">
                                      {item.name.tr}
                                    </h3>
                                    {item.isPopular && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                                    {item.isSignature && <ChefHat className="w-4 h-4 text-primary" />}
                                    {item.isSpicy && <Flame className="w-4 h-4 text-red-500" />}
                                    {item.isVegan && <Leaf className="w-4 h-4 text-green-500" />}
                                    {item.hasVideo && <Video className="w-4 h-4 text-blue-500" />}
                                  </div>
                                  <p className="text-sm text-muted-foreground truncate">
                                    {item.description.tr}
                                  </p>
                                </div>
                                <span className="text-primary font-medium">
                                  {item.price > 0 ? `₺${item.price}` : 'Fiyat Sorunuz'}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setEditingItem(item);
                                      setShowItemModal(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => deleteItem(item.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        )}
      </main>

      {/* Category Modal */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={saveCategory}
      />

      {/* Item Modal */}
      <ItemModal
        isOpen={showItemModal}
        onClose={() => {
          setShowItemModal(false);
          setEditingItem(null);
        }}
        item={editingItem}
        onSave={saveItem}
      />
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ isOpen, onClose, category, onSave }) => {
  const [formData, setFormData] = useState({
    category_id: '',
    name: { tr: '', en: '' },
    description: { tr: '', en: '' },
    image: '',
  });

  useEffect(() => {
    if (category) {
      setFormData({
        category_id: category.category_id || '',
        name: category.name || { tr: '', en: '' },
        description: category.description || { tr: '', en: '' },
        image: category.image || '',
      });
    }
  }, [category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {category?.id ? 'Kategori Düzenle' : 'Yeni Kategori'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!category?.id && (
            <div>
              <label className="text-sm text-muted-foreground">Kategori ID (URL)</label>
              <Input
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                placeholder="ornek-kategori"
                required
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">İsim (TR)</label>
              <Input
                value={formData.name.tr}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, tr: e.target.value } })}
                required
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">İsim (EN)</label>
              <Input
                value={formData.name.en}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">Açıklama (TR)</label>
              <Input
                value={formData.description.tr}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, tr: e.target.value } })}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Açıklama (EN)</label>
              <Input
                value={formData.description.en}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Görsel</label>
            <ImageUploadField
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
              testIdPrefix="category-image"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Item Modal Component
const ItemModal = ({ isOpen, onClose, item, onSave }) => {
  const [formData, setFormData] = useState({
    name: { tr: '', en: '' },
    description: { tr: '', en: '' },
    price: 0,
    image: '',
    allergens: [],
    isPopular: false,
    isSignature: false,
    isSpicy: false,
    isVegan: false,
    isHealthy: false,
    hasVideo: false,
    video: '',
    priceText: null,
  });

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || { tr: '', en: '' },
        description: item.description || { tr: '', en: '' },
        price: item.price || 0,
        image: item.image || '',
        allergens: item.allergens || [],
        isPopular: item.isPopular || false,
        isSignature: item.isSignature || false,
        isSpicy: item.isSpicy || false,
        isVegan: item.isVegan || false,
        isHealthy: item.isHealthy || false,
        hasVideo: item.hasVideo || false,
        video: item.video || '',
        priceText: item.priceText || null,
      });
    }
  }, [item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item?.id ? 'Ürün Düzenle' : 'Yeni Ürün'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">İsim (TR)</label>
              <Input
                value={formData.name.tr}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, tr: e.target.value } })}
                required
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">İsim (EN)</label>
              <Input
                value={formData.name.en}
                onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">Açıklama (TR)</label>
              <Textarea
                value={formData.description.tr}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, tr: e.target.value } })}
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Açıklama (EN)</label>
              <Textarea
                value={formData.description.en}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                rows={2}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">Fiyat (₺)</label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Görsel</label>
              <ImageUploadField
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                testIdPrefix="item-image"
              />
            </div>
          </div>
          
          {/* Video Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Video</label>
              <Switch
                checked={formData.hasVideo}
                onCheckedChange={(checked) => setFormData({ ...formData, hasVideo: checked })}
              />
            </div>
            {formData.hasVideo && (
              <Input
                value={formData.video}
                onChange={(e) => setFormData({ ...formData, video: e.target.value })}
                placeholder="Video URL"
              />
            )}
          </div>

          {/* Badges */}
          <div className="border-t pt-4">
            <label className="text-sm font-medium mb-3 block">Etiketler</label>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" /> Popüler
                </span>
                <Switch
                  checked={formData.isPopular}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPopular: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-primary" /> İmza Ürün
                </span>
                <Switch
                  checked={formData.isSignature}
                  onCheckedChange={(checked) => setFormData({ ...formData, isSignature: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <Flame className="w-4 h-4 text-red-500" /> Acılı
                </span>
                <Switch
                  checked={formData.isSpicy}
                  onCheckedChange={(checked) => setFormData({ ...formData, isSpicy: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <Leaf className="w-4 h-4 text-green-500" /> Vegan
                </span>
                <Switch
                  checked={formData.isVegan}
                  onCheckedChange={(checked) => setFormData({ ...formData, isVegan: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <Heart className="w-4 h-4 text-pink-500" /> Sağlıklı
                </span>
                <Switch
                  checked={formData.isHealthy}
                  onCheckedChange={(checked) => setFormData({ ...formData, isHealthy: checked })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
